import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useJsonLd } from "@/hooks/useJsonLd";
import {
  Flame, ShieldCheck, Users, Award, BookOpen, Bot, Mail, Phone,
  Check, ArrowRight, Printer, Calendar, Instagram,
} from "lucide-react";

const CONTACT_EMAIL = "support@apmfirealarm.com";
const PUBLIC_ORIGIN = "https://apmfirealarm.com";

/**
 * /pitch-deck — Corporate B2B pitch deck.
 *
 * Public page (no auth). Optimized to:
 *   (1) read clean on screen for prospects opening the link
 *   (2) print to PDF cleanly via the Print button (Trevor uses this once and
 *       saves an attachment-friendly version)
 *   (3) track engagement via ?via=<slug> param so /instructor/corporate sees
 *       opens and CTA clicks per company
 */
export default function PitchDeck() {
  const [params] = useSearchParams();
  const via = params.get("via");

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "APM Fire Alarm Exam Prep — Corporate Training",
    "provider": { "@type": "EducationalOrganization", "name": "APM Fire Alarm Exam Prep" },
    "areaServed": "United States",
    "description": "NICET-aligned corporate fire alarm technician training. 140 hours, 15 modules, AI tutor, certificate of completion. Volume pricing for cohorts of 5+.",
  });

  // Fire-and-forget view tracking on mount
  useEffect(() => {
    if (!via) return;
    api.post("/corporate/track-view", { via }).catch(() => { /* non-fatal */ });
  }, [via]);

  const onBookCall = () => {
    if (via) api.post("/corporate/track-cta", { via }).catch(() => {});
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=Corporate%20Training%20Inquiry%20%E2%80%94%20APM%20Fire%20Alarm&body=Hey%20Trevor%2C%0A%0AWe%27re%20interested%20in%20putting%20a%20team%20through%20your%20NICET%20%2F%20S-98%20program.%20Can%20we%20book%2015%20minutes%20to%20discuss%3F%0A%0ACompany%3A%20%5BCOMPANY%5D%0AHeadcount%3A%20%5BNUMBER%5D%0AOptimal%20start%20date%3A%20%5BDATE%5D%0A%0AThanks`;
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Print-only styles to make the PDF version look clean */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page-break { page-break-before: always; }
          body, html { background: #fff !important; }
          .pitch-section { padding-top: 32px !important; padding-bottom: 32px !important; }
        }
      `}</style>

      {/* Sticky action bar (screen-only) */}
      <div className="no-print sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold" data-testid="pitch-home">
            <Flame className="w-5 h-5 text-red-600" />
            <span className="font-heading tracking-tight">APM Fire Alarm</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="btn-outline text-xs" data-testid="pitch-print">
              <Printer className="w-3.5 h-3.5" /> Save as PDF
            </button>
            <button onClick={onBookCall} className="btn-primary text-xs" data-testid="pitch-book-call">
              <Calendar className="w-3.5 h-3.5" /> Book a 15-min call
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-14" data-testid="pitch-deck-page">
        {/* COVER */}
        <section className="pitch-section py-20 sm:py-24">
          <div className="overline mb-4"><span className="red-bar" />Corporate training proposal</div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-6">
            Take your fire alarm techs<br />
            from <span className="line-through text-zinc-400">Certified</span> to <span className="text-red-600">NICET-certified</span>.
          </h1>
          <p className="text-xl text-zinc-700 max-w-3xl leading-relaxed mb-8">
            APM is a 140-hour structured fire alarm technician program built and taught by a
            15-year NYC veteran. Your team finishes <strong>NICET Level 1 ready</strong> in
            8&ndash;12 weeks &mdash; with measurable accountability the whole way.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={onBookCall} className="btn-primary" data-testid="pitch-cover-cta">
              <Calendar className="w-4 h-4" /> Book a 15-min discovery call <ArrowRight className="w-4 h-4" />
            </button>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-zinc-700 hover:text-red-600 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
            </a>
          </div>
        </section>

        {/* THE PROBLEM */}
        <section className="pitch-section py-16 border-t border-zinc-200 print-page-break">
          <div className="overline mb-3"><span className="red-bar" />The cost of un-certified techs</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">Three numbers your CFO already knows.</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: "$12k+", t: "Hourly billing differential between a NICET-certified Level 2 tech and an uncertified equivalent on a NYC commercial install. Per tech, per quarter." },
              { n: "63%", t: "of NICET Level 1 first-time candidates fail without structured prep. Each retake costs $250 + 6 weeks of lost productivity." },
              { n: "0", t: "fire alarm-specific training programs exist within the IBEW / ABC curriculum. Techs learn on the job — slowly, expensively, inconsistently." },
            ].map((b) => (
              <div key={b.n} className="border-l-4 border-red-600 pl-5 py-2">
                <div className="font-heading text-5xl font-bold tracking-tighter mb-2 text-red-600">{b.n}</div>
                <p className="text-sm text-zinc-700 leading-relaxed">{b.t}</p>
              </div>
            ))}
          </div>
        </section>

        {/* THE SOLUTION */}
        <section className="pitch-section py-16 border-t border-zinc-200">
          <div className="overline mb-3"><span className="red-bar" />The APM solution</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">A structured program. Not a video library.</h2>
          <p className="text-lg text-zinc-700 max-w-3xl leading-relaxed mb-10">
            APM is purpose-built for the fire alarm trade &mdash; 15 modules covering everything
            from NFPA 72 fundamentals to FACU panel programming, NYC S-98, and 50-question
            final exam preparation. Plus an AI tutor your team can ask code questions to 24/7.
          </p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {[
              { I: BookOpen, t: "140 hours of written curriculum across 15 core modules" },
              { I: ShieldCheck, t: "NICET Level 1 Foundation prep with battery sizing, spacing math, code navigation drills" },
              { I: Award, t: "NYC S-98 Certificate of Fitness review module (75-question prep + scenario walkthroughs)" },
              { I: Bot, t: "Unlimited AI Tutor (Claude Sonnet 4.5) — your team asks code questions, gets NFPA citations back" },
              { I: Users, t: "7 specialty tracks: S-12 Citywide Sprinkler, S-13, S-95, plus core fire alarm" },
              { I: Check, t: "Interactive FACU panel simulator + riser diagram lab (browser-based, no hardware setup)" },
            ].map((f) => (
              <div key={f.t} className="flex items-start gap-3 py-2">
                <f.I className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-800 leading-relaxed">{f.t}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SOCIAL PROOF / INSTRUCTOR */}
        <section className="pitch-section py-16 border-t border-zinc-200 print-page-break">
          <div className="overline mb-3"><span className="red-bar" />Who's behind it</div>
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-1">
              <div className="aspect-square bg-gradient-to-br from-red-600 to-red-700 grid place-items-center text-white font-heading text-7xl font-bold mb-3">
                T
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Lead instructor</div>
              <div className="font-heading text-xl font-bold mt-1">Trevor Marson</div>
              <div className="text-xs text-zinc-600 mt-1">15+ years NYC fire alarm field experience</div>
            </div>
            <div className="md:col-span-2 space-y-4 text-zinc-700">
              <p className="leading-relaxed">
                Trevor spent the last 15 years installing fire alarm in NYC &mdash; from
                Lower-East-Side co-op residentials to 80-story commercial high-rises in
                Midtown. He's wired Notifier, Simplex, Edwards, FCI, Honeywell. He's
                taught 40+ apprentices through NICET certification.
              </p>
              <p className="leading-relaxed">
                Every lesson in APM is something Trevor's been called in to fix at 2am on a
                Sunday because someone less experienced got it wrong. The curriculum isn't
                academic &mdash; it's <strong>"the things that go wrong in real installs"</strong>.
              </p>
              <p className="leading-relaxed">
                Direct line: every student can email Trevor and get a personal response.
                Your team doesn't get lost in a forum.
              </p>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="pitch-section py-16 border-t border-zinc-200">
          <div className="overline mb-3"><span className="red-bar" />Cohort pricing</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">Built for teams, priced for ROI.</h2>
          <p className="text-zinc-700 max-w-3xl leading-relaxed mb-8">
            Per-seat retail is <strong>$149 lifetime access</strong>. For cohorts of 5+ we
            offer volume pricing that pays back from day one of the first certified tech.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { n: "5–9 seats", p: "$129", per: "/ seat", v: "Standard cohort discount" },
              { n: "10–24 seats", p: "$99", per: "/ seat", v: "Most popular &mdash; volume pricing", hot: true },
              { n: "25+ seats", p: "Custom", per: "", v: "Includes optional 1-hr kickoff Zoom" },
            ].map((t) => (
              <div key={t.n} className={`p-5 border-2 ${t.hot ? "border-red-600 bg-red-50/30" : "border-zinc-200"}`}>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{t.n}</div>
                <div className="font-heading text-4xl font-bold tracking-tighter">{t.p}<span className="text-base font-normal text-zinc-500">{t.per}</span></div>
                <div className="text-xs text-zinc-700 mt-3" dangerouslySetInnerHTML={{ __html: t.v }} />
              </div>
            ))}
          </div>
          <div className="mt-6 p-4 bg-zinc-50 border-l-4 border-zinc-300 text-sm text-zinc-700 leading-relaxed">
            <strong>ROI math:</strong> A NICET Level 1 cert bumps a tech's billable rate ~$8–12/hr.
            For a 40-hr work week, that's <strong>$320–480/week per tech</strong>. A 10-seat
            cohort at $99/seat = $990 total &mdash; recouped within the first 2&ndash;3 weeks
            of your first certified tech billing at the higher rate.
          </div>
        </section>

        {/* TIMELINE */}
        <section className="pitch-section py-16 border-t border-zinc-200 print-page-break">
          <div className="overline mb-3"><span className="red-bar" />What happens next</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">From signed PO to first cert in 12 weeks.</h2>
          <ol className="space-y-5">
            {[
              { wk: "Wk 0", t: "Discovery call (15 min). Confirm cohort size, target start date, certification goal (NICET I / II / S-98)." },
              { wk: "Wk 1", t: "PO signed. Team gets enrollment links. We schedule an optional 1-hr kickoff Zoom (>=25 seats includes; smaller cohorts add for $300)." },
              { wk: "Wk 2–9", t: "Self-paced study. Your team works through 15 modules + specialty tracks. Trevor's available via direct email + AI Tutor 24/7." },
              { wk: "Wk 10", t: "Final practice exam (50-question simulated). 80%+ = certificate-ready." },
              { wk: "Wk 11–12", t: "Your team books their NICET exam. We coordinate with you on results and provide retake support if needed." },
            ].map((s) => (
              <li key={s.wk} className="flex gap-5 items-start">
                <div className="font-mono text-xs uppercase tracking-widest text-red-600 font-bold flex-shrink-0 pt-1 w-16">{s.wk}</div>
                <div className="text-sm text-zinc-800 leading-relaxed flex-1">{s.t}</div>
              </li>
            ))}
          </ol>
        </section>

        {/* CTA */}
        <section className="pitch-section py-20 border-t border-zinc-200">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4">Let's run the numbers for your team.</h2>
          <p className="text-lg text-zinc-700 max-w-2xl leading-relaxed mb-8">
            15-minute call. I'll quote your exact cohort price, share 2 sample modules, and
            we'll decide together if APM is the right fit for your team's training budget.
            No deck-of-the-week energy &mdash; you'll be talking to Trevor directly.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <button onClick={onBookCall} className="btn-primary" data-testid="pitch-final-cta">
              <Calendar className="w-4 h-4" /> Book the call <ArrowRight className="w-4 h-4" />
            </button>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-zinc-700 hover:text-red-600 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {CONTACT_EMAIL}
            </a>
            <a href={`${PUBLIC_ORIGIN}/corporate-training`} className="text-sm text-zinc-700 hover:text-red-600 inline-flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Online quote form
            </a>
          </div>
        </section>

        <footer className="py-10 border-t border-zinc-200 text-xs text-zinc-500 flex flex-wrap items-center justify-between gap-3">
          <div>APM Fire Alarm Exam Prep &middot; New York, NY</div>
          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/apmfireexam_prep"
              target="_blank"
              rel="noopener noreferrer"
              className="no-print inline-flex items-center gap-1.5 text-zinc-600 hover:text-red-600 transition-colors"
              aria-label="Follow APM Fire Alarm Exam Prep on Instagram"
              data-testid="pitch-footer-instagram"
            >
              <Instagram className="w-3.5 h-3.5" />
              <span className="font-mono uppercase tracking-widest">@apmfireexam_prep</span>
            </a>
            <div className="font-mono uppercase tracking-widest">apmfirealarm.com</div>
          </div>
        </footer>
      </main>
    </div>
  );
}
